import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LocalizedLink,
  LocalizedNavLink,
  localizePath,
  stripLangPrefix,
} from "@/i18n/language";
import { getBasePrefix } from "@/lib/seo";
import { img } from "@/lib/images";
import AvailabilityModal from "@/pages/home/components/availability/AvailabilityModal";

const weddingSubLinks = [
  { key: "wedding_cat_allgemein_title", slug: "general-wedding-information" },
  { key: "wedding_cat_standesamt_title", slug: "wedding-with-registry-office" },
  { key: "wedding_cat_ohne_standesamt_title", slug: "wedding-without-registry-office" },
  { key: "wedding_cat_eheversprechen_title", slug: "vow-renewal" },
  { key: "wedding_cat_jubilaeum_title", slug: "wedding-anniversary" },
];

const activitiesSubLinks = [
  { key: "nav_tour_info_gallery_label", to: "/tour-info" },
  { key: "tour_category_daytrips_title", to: "/day-trips" },
  { key: "tour_category_multiday_title", to: "/multi-day-tours" },
  { key: "tour_category_adventure_title", to: "/adventure-tours" },
  { key: "nav_activities_shopping", to: "/shopping-bali" },
];

const baliInfoSubLinks = [
  { key: "bali_info_sidebar_general", slug: "general" },
  { key: "bali_info_sidebar_about", slug: "about-bali" },
  { key: "bali_info_sidebar_books", slug: "books" },
  { key: "bali_info_card_plants_title", slug: "plants" },
  { key: "bali_info_card_fruits_title", slug: "fruits" },
  { key: "bali_info_card_spices_title", slug: "spices" },
  { key: "bali_info_card_rice_title", slug: "rice" },
];

const villaSubLinks = [
  { key: "villa_info_title", to: "/villa-info" },
  { key: "nav_villa_location", to: "/villa-location" },
  { key: "villas_room_1_title", to: "/villa-frangipani" },
  { key: "villas_room_2_title", to: "/villa-bougainvillea" },
  { key: "villas_room_3_title", to: "/villa-hibiscus" },
  { key: "nav_activities_health", to: "/health-sports" },
];

export default function Navbar({ solid = false }: { solid?: boolean }) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [weddingDropOpen, setWeddingDropOpen] = useState(false);
  const [activitiesDropOpen, setActivitiesDropOpen] = useState(false);
  const [baliInfoDropOpen, setBaliInfoDropOpen] = useState(false);
  const [aboutDropOpen, setAboutDropOpen] = useState(false);
  const [villaDropOpen, setVillaDropOpen] = useState(false);
  const [mobileVillaOpen, setMobileVillaOpen] = useState(false);
  const [availOpen, setAvailOpen] = useState(false);
  const villaRef = useRef<HTMLDivElement>(null);
  const [mobileWeddingOpen, setMobileWeddingOpen] = useState(false);
  const [mobileActivitiesOpen, setMobileActivitiesOpen] = useState(false);
  const [mobileBaliInfoOpen, setMobileBaliInfoOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const weddingRef = useRef<HTMLDivElement>(null);
  const activitiesRef = useRef<HTMLDivElement>(null);
  const baliInfoRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (weddingRef.current && !weddingRef.current.contains(e.target as Node)) {
        setWeddingDropOpen(false);
      }
      if (activitiesRef.current && !activitiesRef.current.contains(e.target as Node)) {
        setActivitiesDropOpen(false);
      }
      if (baliInfoRef.current && !baliInfoRef.current.contains(e.target as Node)) {
        setBaliInfoDropOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(e.target as Node)) {
        setAboutDropOpen(false);
      }
      if (villaRef.current && !villaRef.current.contains(e.target as Node)) {
        setVillaDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setWeddingDropOpen(false);
    setActivitiesDropOpen(false);
    setBaliInfoDropOpen(false);
    setAboutDropOpen(false);
    setVillaDropOpen(false);
    setMobileAboutOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  // Sprachneutraler Pfad (immer ohne "/en") – Grundlage für die aktiven
  // Zustände der Navigation und für den Sprachwechsel.
  const basePath = stripLangPrefix(location.pathname);

  // Sprache direkt aus der URL ableiten (nicht aus i18n), damit der Umschalter
  // schon während des Seitenwechsels korrekt steht.
  const currentLng: "en" | "de" =
    location.pathname === "/en" || location.pathname.startsWith("/en/")
      ? "en"
      : "de";

  /** Achte, immer gültige Adresse der jeweils anderen Sprachversion. */
  const langHref = (lng: "en" | "de") =>
    `${getBasePrefix()}${localizePath(basePath, lng)}`;

  const switchLang = (lng: "en" | "de") => {
    if (lng === currentLng) return;
    localStorage.setItem("lng", lng);
    // Suchparameter (z. B. ?voucher=…) bleiben beim Sprachwechsel erhalten.
    navigate(`${localizePath(basePath, lng)}${location.search}${location.hash}`);
  };
  // `solid` forces the dark bar on pages that have no dark hero at the top
  // (otherwise the light nav text would be invisible on a light background).
  const isSolid = scrolled || solid;
  const isWeddingActive = basePath.startsWith("/weddings");
  const isActivitiesActive =
    basePath.startsWith("/activities") ||
    basePath.startsWith("/tour-info") ||
    basePath.startsWith("/day-trips") ||
    basePath.startsWith("/multi-day-tours") ||
    basePath.startsWith("/adventure-tours") ||
    basePath.startsWith("/tour/") ||
    basePath.startsWith("/shopping-bali");
  const isBaliInfoActive = basePath.startsWith("/bali-info");
  const isAboutActive =
    basePath === "/about" ||
    basePath.startsWith("/nostalgie") ||
    basePath.startsWith("/vw181-kuebelwagen");
  const isVillaActive =
    basePath.startsWith("/villa") ||
    basePath === "/villas" ||
    basePath.startsWith("/health-sports");

  const closeAllDesktop = () => {
    setVillaDropOpen(false);
    setWeddingDropOpen(false);
    setActivitiesDropOpen(false);
    setBaliInfoDropOpen(false);
    setAboutDropOpen(false);
  };

  const closeAllMobile = () => {
    setMobileVillaOpen(false);
    setMobileWeddingOpen(false);
    setMobileActivitiesOpen(false);
    setMobileBaliInfoOpen(false);
    setMobileAboutOpen(false);
  };

  const openVilla = () => {
    closeAllDesktop();
    setVillaDropOpen(true);
  };
  const openWedding = () => {
    closeAllDesktop();
    setWeddingDropOpen(true);
  };
  const openActivities = () => {
    closeAllDesktop();
    setActivitiesDropOpen(true);
  };
  const openBaliInfo = () => {
    closeAllDesktop();
    setBaliInfoDropOpen(true);
  };
  const openAbout = () => {
    closeAllDesktop();
    setAboutDropOpen(true);
  };
  const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors ${
      isActive
        ? "text-accent-300"
        : "text-background-50/90 hover:text-accent-300"
    }`;

  const dropdownItemClass = (isActive: boolean) =>
    `block px-5 py-2.5 text-base transition-colors border-b border-accent-100 last:border-0 ${
      isActive
        ? "bg-accent-100 text-accent-800 font-medium"
        : "text-foreground-700 hover:bg-accent-100 hover:text-accent-800"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? "bg-primary-700/95 backdrop-blur-md border-b border-background-50/15"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex lg:grid lg:grid-cols-[1fr_auto_1fr] items-center justify-between h-16 md:h-24 gap-4">
        {/* Logo */}
        <LocalizedLink to="/" className="flex items-center cursor-pointer">
          <div className="h-12 md:h-[72px] max-w-[250px] md:max-w-[420px] flex items-center justify-start -ml-1 md:-ml-2">
            <img
              src={img("balivwtour/brand/logo-balivwtour-01.png")}
              alt="Bali VW Tour Logo"
              title="Bali VW Tour"
              className="h-full w-auto max-w-full object-contain object-left"
            />
          </div>
        </LocalizedLink>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center justify-center gap-4 xl:gap-7">
          {/* Aktivitäten dropdown */}
          <div ref={activitiesRef} className="relative">
            <button
              onMouseEnter={openActivities}
              onClick={() => (activitiesDropOpen ? setActivitiesDropOpen(false) : openActivities())}
              className={`text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1 ${
                isActivitiesActive
                  ? "text-accent-300"
                  : "text-background-50/90 hover:text-accent-300"
              }`}
            >
              {t("nav_activities")}
              <i className={`ri-add-line text-base transition-transform ${activitiesDropOpen ? "rotate-45" : ""}`}></i>
            </button>
            {activitiesDropOpen && (
              <div
                onMouseLeave={() => setActivitiesDropOpen(false)}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 border border-background-200 rounded-lg overflow-hidden z-50"
              >
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-background-50 border-l border-t border-background-200 rotate-45"></div>
                <LocalizedLink
                  to="/activities"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-accent-800 bg-accent-50/60 hover:bg-accent-100 transition-colors border-b border-accent-100"
                >
                  {t("nav_overview_activities")}
                </LocalizedLink>
                {activitiesSubLinks.map((sub) => (
                  <LocalizedLink
                    key={sub.to}
                    to={sub.to}
                    className={dropdownItemClass(basePath === sub.to)}
                  >
                    {t(sub.key)}
                  </LocalizedLink>
                ))}
                <LocalizedLink
                  to="/booking"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 transition-colors"
                >
                  {t("nav_book_daytrips")}
                  <i className="ri-calendar-check-line"></i>
                </LocalizedLink>
                <LocalizedLink
                  to="/booking-multiday"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 transition-colors border-t border-background-50/20"
                >
                  {t("nav_book_multiday")}
                  <i className="ri-map-2-line"></i>
                </LocalizedLink>
              </div>
            )}
          </div>

          {/* Reise-Bausteine */}
          <LocalizedNavLink to="/your-stay" className={desktopLinkClass}>
            {t("nav_bausteine")}
          </LocalizedNavLink>

          {/* Bali Info dropdown */}
          <div ref={baliInfoRef} className="relative">
            <button
              onMouseEnter={openBaliInfo}
              onClick={() => (baliInfoDropOpen ? setBaliInfoDropOpen(false) : openBaliInfo())}
              className={`text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1 ${
                isBaliInfoActive
                  ? "text-accent-300"
                  : "text-background-50/90 hover:text-accent-300"
              }`}
            >
              {t("nav_bali_info")}
              <i className={`ri-add-line text-base transition-transform ${baliInfoDropOpen ? "rotate-45" : ""}`}></i>
            </button>
            {baliInfoDropOpen && (
              <div
                onMouseLeave={() => setBaliInfoDropOpen(false)}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 border border-background-200 rounded-lg overflow-hidden z-50"
              >
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-background-50 border-l border-t border-background-200 rotate-45"></div>
                <LocalizedLink
                  to="/bali-info"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-accent-800 bg-accent-50/60 hover:bg-accent-100 transition-colors border-b border-accent-100"
                >
                  {t("nav_overview_bali_info")}
                </LocalizedLink>
                {baliInfoSubLinks.map((sub) => (
                  <LocalizedLink
                    key={sub.slug}
                    to={`/bali-info/${sub.slug}`}
                    className={dropdownItemClass(basePath === `/bali-info/${sub.slug}`)}
                  >
                    {t(sub.key)}
                  </LocalizedLink>
                ))}
              </div>
            )}
          </div>

          {/* Villen dropdown */}
          <div ref={villaRef} className="relative">
            <button
              onMouseEnter={openVilla}
              onClick={() => (villaDropOpen ? setVillaDropOpen(false) : openVilla())}
              className={`text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1 ${
                isVillaActive
                  ? "text-accent-300"
                  : "text-background-50/90 hover:text-accent-300"
              }`}
            >
              {t("nav_villas")}
              <i className={`ri-add-line text-base transition-transform ${villaDropOpen ? "rotate-45" : ""}`}></i>
            </button>
            {villaDropOpen && (
              <div
                onMouseLeave={() => setVillaDropOpen(false)}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 border border-background-200 rounded-lg overflow-hidden z-50"
              >
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-background-50 border-l border-t border-background-200 rotate-45"></div>
                <LocalizedLink
                  to="/villas"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-accent-800 bg-accent-50/60 hover:bg-accent-100 transition-colors border-b border-accent-100"
                >
                  {t("nav_overview_villas")}
                </LocalizedLink>
                {villaSubLinks.map((sub) => (
                  <LocalizedLink
                    key={sub.to}
                    to={sub.to}
                    className={dropdownItemClass(basePath === sub.to)}
                  >
                    {t(sub.key)}
                  </LocalizedLink>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setVillaDropOpen(false);
                    setAvailOpen(true);
                  }}
                  className="flex w-full items-center justify-between px-5 py-3 text-base font-semibold text-foreground-950 bg-secondary-300 hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {t("nav_check_availability")}
                  <i className="ri-calendar-line"></i>
                </button>
                <LocalizedLink
                  to="/villa-reservation"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 transition-colors border-t border-background-50/20"
                >
                  {t("nav_book_villa")}
                  <i className="ri-calendar-check-line"></i>
                </LocalizedLink>
              </div>
            )}
          </div>

          {/* Hochzeit dropdown */}
          <div ref={weddingRef} className="relative">
            <button
              onMouseEnter={openWedding}
              onClick={() => (weddingDropOpen ? setWeddingDropOpen(false) : openWedding())}
              className={`text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1 ${
                isWeddingActive
                  ? "text-accent-300"
                  : "text-background-50/90 hover:text-accent-300"
              }`}
            >
              {t("nav_weddings")}
              <i className={`ri-add-line text-base transition-transform ${weddingDropOpen ? "rotate-45" : ""}`}></i>
            </button>
            {weddingDropOpen && (
              <div
                onMouseLeave={() => setWeddingDropOpen(false)}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 border border-background-200 rounded-lg overflow-hidden z-50"
              >
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-background-50 border-l border-t border-background-200 rotate-45"></div>
                <LocalizedLink
                  to="/weddings"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-accent-800 bg-accent-50/60 hover:bg-accent-100 transition-colors border-b border-accent-100"
                >
                  {t("nav_overview_weddings")}
                </LocalizedLink>
                {weddingSubLinks.map((sub) => (
                  <LocalizedLink
                    key={sub.slug}
                    to={`/weddings/${sub.slug}`}
                    className={dropdownItemClass(basePath === `/weddings/${sub.slug}`)}
                  >
                    {t(sub.key)}
                  </LocalizedLink>
                ))}
                <LocalizedLink
                  to="/wedding-inquiry"
                  className="flex items-center justify-between px-5 py-3 text-base font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 transition-colors"
                >
                  {t("nav_book_wedding")}
                  <i className="ri-heart-line"></i>
                </LocalizedLink>
              </div>
            )}
          </div>

          {/* Über uns dropdown */}
          <div ref={aboutRef} className="relative">
            <button
              onMouseEnter={openAbout}
              onClick={() => (aboutDropOpen ? setAboutDropOpen(false) : openAbout())}
              className={`text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1 ${
                isAboutActive
                  ? "text-accent-300"
                  : "text-background-50/90 hover:text-accent-300"
              }`}
              aria-haspopup="true"
              aria-expanded={aboutDropOpen}
            >
              {t("nav_about")}
              <i className={`ri-add-line text-base transition-transform ${aboutDropOpen ? "rotate-45" : ""}`}></i>
            </button>
            {aboutDropOpen && (
              <div
                onMouseLeave={() => setAboutDropOpen(false)}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 border border-background-200 rounded-lg overflow-hidden z-50"
              >
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-background-50 border-l border-t border-background-200 rotate-45"></div>
                <LocalizedLink
                  to="/about"
                  className={dropdownItemClass(basePath === "/about")}
                >
                  {t("nav_about_gery")}
                </LocalizedLink>
                <LocalizedLink
                  to="/nostalgie"
                  className={dropdownItemClass(basePath === "/nostalgie")}
                >
                  {t("nav_nostalgia")}
                </LocalizedLink>
                <LocalizedLink
                  to="/vw181-kuebelwagen"
                  className={dropdownItemClass(
                    basePath === "/vw181-kuebelwagen",
                  )}
                >
                  {t("nav_kuebelwagen")}
                </LocalizedLink>
              </div>
            )}
          </div>

          <LocalizedNavLink to="/contact" className={desktopLinkClass}>
            {t("nav_contact")}
          </LocalizedNavLink>
        </nav>

        {/* Right side: lang + book + burger */}
        <div className="flex items-center gap-1 md:gap-3 lg:justify-end">
          <div
            className={`flex items-center rounded-full p-1 text-[10px] md:text-xs font-medium ${
              isSolid ? "bg-background-50/10 border border-background-50/20" : "bg-background-950/30 backdrop-blur"
            }`}
          >
            <a
              href={langHref("en")}
              hrefLang="en"
              lang="en"
              aria-label="Switch to English"
              aria-current={currentLng === "en" ? "true" : undefined}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                switchLang("en");
              }}
              className={`px-2 md:px-3 py-1 rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                currentLng === "en"
                  ? "bg-accent-500 text-white font-semibold ring-1 ring-background-50/70"
                  : "text-background-50/80 hover:text-background-50"
              }`}
            >
              EN
            </a>
            <a
              href={langHref("de")}
              hrefLang="de"
              lang="de"
              aria-label="Zur deutschen Version wechseln"
              aria-current={currentLng === "de" ? "true" : undefined}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                switchLang("de");
              }}
              className={`px-2 md:px-3 py-1 rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                currentLng === "de"
                  ? "bg-accent-500 text-white font-semibold ring-1 ring-background-50/70"
                  : "text-background-50/80 hover:text-background-50"
              }`}
            >
              DE
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ring-1 ${
              mobileOpen
                ? "bg-red-600 text-white ring-background-50/70"
                : "text-background-50 ring-background-50/25 hover:ring-background-50/50"
            }`}
          >
            <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* ───────────── MOBILE MENU ───────────── */}
      {mobileOpen && (
        <div className="lg:hidden absolute top-full left-3 right-3 mt-2 bg-background-50 border border-background-200 rounded-lg z-50">
          <nav className="px-3 py-3">

            {/* Aktivitäten accordion */}
            <div>
              <button
                onClick={() => {
                  if (mobileActivitiesOpen) setMobileActivitiesOpen(false);
                  else { closeAllMobile(); setMobileActivitiesOpen(true); }
                }}
                className={`w-full flex items-center justify-between py-2.5 px-2 text-base font-medium cursor-pointer ${
                  isActivitiesActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
                }`}
              >
                {t("nav_activities")}
                <i className={`ri-add-line text-base transition-transform ${mobileActivitiesOpen ? "rotate-45" : ""}`}></i>
              </button>
              {mobileActivitiesOpen && (
                <div className="ml-3 border-l-2 border-accent-200 pl-2 mb-1">
                  <LocalizedNavLink
                    to="/activities"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block py-2 text-sm cursor-pointer ${
                        isActive && basePath === "/activities"
                          ? "text-primary-600 font-medium"
                          : "text-foreground-600 hover:text-primary-600"
                      }`
                    }
                  >
                    {t("nav_overview_activities")}
                  </LocalizedNavLink>
                  {activitiesSubLinks.map((sub) => (
                    <LocalizedNavLink
                      key={sub.to}
                      to={sub.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `block py-2 text-sm cursor-pointer ${
                          isActive ? "text-primary-600 font-medium" : "text-foreground-600 hover:text-primary-600"
                        }`
                      }
                    >
                      {t(sub.key)}
                    </LocalizedNavLink>
                  ))}
                  <LocalizedNavLink
                    to="/booking"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between py-2.5 text-sm font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 rounded px-2 mt-1 cursor-pointer"
                  >
                    {t("nav_book_daytrips")}
                    <i className="ri-calendar-check-line"></i>
                  </LocalizedNavLink>
                  <LocalizedNavLink
                    to="/booking-multiday"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between py-2.5 text-sm font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 rounded px-2 mt-1 cursor-pointer"
                  >
                    {t("nav_book_multiday")}
                    <i className="ri-map-2-line"></i>
                  </LocalizedNavLink>
                </div>
              )}
            </div>

            {/* Reise-Bausteine */}
            <LocalizedNavLink
              to="/your-stay"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block py-2.5 px-2 text-base font-medium cursor-pointer rounded ${
                  isActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
                }`
              }
            >
              {t("nav_bausteine")}
            </LocalizedNavLink>

            {/* Bali Info accordion */}
            <div>
              <button
                onClick={() => {
                  if (mobileBaliInfoOpen) setMobileBaliInfoOpen(false);
                  else { closeAllMobile(); setMobileBaliInfoOpen(true); }
                }}
                className={`w-full flex items-center justify-between py-2.5 px-2 text-base font-medium cursor-pointer ${
                  isBaliInfoActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
                }`}
              >
                {t("nav_bali_info")}
                <i className={`ri-add-line text-base transition-transform ${mobileBaliInfoOpen ? "rotate-45" : ""}`}></i>
              </button>
              {mobileBaliInfoOpen && (
                <div className="ml-3 border-l-2 border-accent-200 pl-2 mb-1">
                  <LocalizedNavLink
                    to="/bali-info"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block py-2 text-sm cursor-pointer ${
                        isActive && basePath === "/bali-info"
                          ? "text-primary-600 font-medium"
                          : "text-foreground-600 hover:text-primary-600"
                      }`
                    }
                  >
                    {t("nav_overview_bali_info")}
                  </LocalizedNavLink>
                  {baliInfoSubLinks.map((sub) => (
                    <LocalizedNavLink
                      key={sub.slug}
                      to={`/bali-info/${sub.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `block py-2 text-sm cursor-pointer ${
                          isActive ? "text-primary-600 font-medium" : "text-foreground-600 hover:text-primary-600"
                        }`
                      }
                    >
                      {t(sub.key)}
                    </LocalizedNavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Villa accordion */}
            <div>
              <button
                onClick={() => {
                  if (mobileVillaOpen) setMobileVillaOpen(false);
                  else { closeAllMobile(); setMobileVillaOpen(true); }
                }}
                className={`w-full flex items-center justify-between py-2.5 px-2 text-base font-medium cursor-pointer ${
                  isVillaActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
                }`}
              >
                {t("nav_villas")}
                <i className={`ri-add-line text-base transition-transform ${mobileVillaOpen ? "rotate-45" : ""}`}></i>
              </button>
              {mobileVillaOpen && (
                <div className="ml-3 border-l-2 border-accent-200 pl-2 mb-1">
                  <LocalizedNavLink
                    to="/villas"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block py-2 text-sm cursor-pointer ${
                        isActive && basePath === "/villas"
                          ? "text-primary-600 font-medium"
                          : "text-foreground-600 hover:text-primary-600"
                      }`
                    }
                  >
                    {t("nav_overview_villas")}
                  </LocalizedNavLink>
                  {villaSubLinks.map((sub) => (
                    <LocalizedNavLink
                      key={sub.to}
                      to={sub.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `block py-2 text-sm cursor-pointer ${
                          isActive ? "text-primary-600 font-medium" : "text-foreground-600 hover:text-primary-600"
                        }`
                      }
                    >
                      {t(sub.key)}
                    </LocalizedNavLink>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      setAvailOpen(true);
                    }}
                    className="flex w-full items-center justify-between py-2.5 text-sm font-semibold text-foreground-950 bg-secondary-300 hover:bg-secondary-200 rounded px-2 mt-1 cursor-pointer"
                  >
                    {t("nav_check_availability")}
                    <i className="ri-calendar-line"></i>
                  </button>
                  <LocalizedNavLink
                    to="/villa-reservation"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between py-2.5 text-sm font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 rounded px-2 mt-1 cursor-pointer"
                  >
                    {t("nav_book_villa")}
                    <i className="ri-calendar-check-line"></i>
                  </LocalizedNavLink>
                </div>
              )}
            </div>

            {/* Hochzeit accordion */}
            <div>
              <button
                onClick={() => {
                  if (mobileWeddingOpen) setMobileWeddingOpen(false);
                  else { closeAllMobile(); setMobileWeddingOpen(true); }
                }}
                className={`w-full flex items-center justify-between py-2.5 px-2 text-base font-medium cursor-pointer ${
                  isWeddingActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
                }`}
              >
                {t("nav_weddings")}
                <i className={`ri-add-line text-base transition-transform ${mobileWeddingOpen ? "rotate-45" : ""}`}></i>
              </button>
              {mobileWeddingOpen && (
                <div className="ml-3 border-l-2 border-accent-200 pl-2 mb-1">
                  <LocalizedNavLink
                    to="/weddings"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block py-2 text-sm cursor-pointer ${
                        isActive && basePath === "/weddings"
                          ? "text-primary-600 font-medium"
                          : "text-foreground-600 hover:text-primary-600"
                      }`
                    }
                  >
                    {t("nav_overview_weddings")}
                  </LocalizedNavLink>
                  {weddingSubLinks.map((sub) => (
                    <LocalizedNavLink
                      key={sub.slug}
                      to={`/weddings/${sub.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `block py-2 text-sm cursor-pointer ${
                          isActive ? "text-primary-600 font-medium" : "text-foreground-600 hover:text-primary-600"
                        }`
                      }
                    >
                      {t(sub.key)}
                    </LocalizedNavLink>
                  ))}
                  <LocalizedNavLink
                    to="/wedding-inquiry"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between py-2.5 text-sm font-semibold text-foreground-950 bg-accent-300 hover:bg-accent-200 rounded px-2 mt-1 cursor-pointer"
                  >
                    {t("nav_book_wedding")}
                    <i className="ri-heart-line"></i>
                  </LocalizedNavLink>
                </div>
              )}
            </div>

            {/* Über uns accordion */}
            <div>
              <button
                onClick={() => {
                  if (mobileAboutOpen) setMobileAboutOpen(false);
                  else { closeAllMobile(); setMobileAboutOpen(true); }
                }}
                className={`w-full flex items-center justify-between py-2.5 px-2 text-base font-medium cursor-pointer ${
                  isAboutActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
                }`}
                aria-expanded={mobileAboutOpen}
              >
                {t("nav_about")}
                <i className={`ri-add-line text-base transition-transform ${mobileAboutOpen ? "rotate-45" : ""}`}></i>
              </button>
              {mobileAboutOpen && (
                <div className="ml-3 border-l-2 border-accent-200 pl-2 mb-1">
                  <LocalizedLink
                    to="/about"
                    onClick={() => setMobileOpen(false)}
                    className={`block py-2 text-sm cursor-pointer ${
                      basePath === "/about"
                        ? "text-primary-600 font-medium"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {t("nav_about_gery")}
                  </LocalizedLink>
                  <LocalizedLink
                    to="/nostalgie"
                    onClick={() => setMobileOpen(false)}
                    className={`block py-2 text-sm cursor-pointer ${
                      basePath === "/nostalgie"
                        ? "text-primary-600 font-medium"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {t("nav_nostalgia")}
                  </LocalizedLink>
                  <LocalizedLink
                    to="/vw181-kuebelwagen"
                    onClick={() => setMobileOpen(false)}
                    className={`block py-2 text-sm cursor-pointer ${
                      basePath === "/vw181-kuebelwagen"
                        ? "text-primary-600 font-medium"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {t("nav_kuebelwagen")}
                  </LocalizedLink>
                </div>
              )}
            </div>

            {/* Kontakt */}
            <LocalizedNavLink
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block py-2.5 px-2 text-base font-medium cursor-pointer rounded ${
                  isActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
                }`
              }
            >
              {t("nav_contact")}
            </LocalizedNavLink>

          </nav>
        </div>
      )}

      <AvailabilityModal open={availOpen} onClose={() => setAvailOpen(false)} />
    </header>
  );
}